import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"

// Jump tags on Academy Home: click scrolls a section into view, and the
// active tag tracks whichever section is currently nearest the sticky row.
export default class extends Controller {
  static targets = ["chrome", "tag"]

  connect() {
    this.pinnedTag = null
    this.onScroll = () => this.scheduleSync()
    this.onScrollGesture = () => { this.pinnedTag = null }

    // Capture phase: an element's scroll does not bubble to window.
    window.addEventListener("scroll", this.onScroll, { passive: true, capture: true })
    window.addEventListener("wheel", this.onScrollGesture, { passive: true })
    window.addEventListener("touchmove", this.onScrollGesture, { passive: true })

    const hashId = window.location.hash.replace(/^#/, "")
    if (hashId) {
      const tag = this.tagTargets.find((el) => el.getAttribute("href") === `#${hashId}`)
      if (tag) this.pinnedTag = tag
    }

    this.syncActive()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll, { capture: true })
    window.removeEventListener("wheel", this.onScrollGesture)
    window.removeEventListener("touchmove", this.onScrollGesture)
    if (this.spyFrame) cancelAnimationFrame(this.spyFrame)
  }

  // Clicking a tag is the choice of section, so hold it current until the
  // next scroll gesture: a short last section may never cross the spy line.
  jump(event) {
    event.preventDefault()

    const tag = event.currentTarget
    const section = this.sectionFor(tag)
    if (!section) return

    this.pinnedTag = tag
    this.markActive(tag)
    section.scrollIntoView({ behavior: this.scrollBehavior(), block: "start" })

    const id = (tag.getAttribute("href") || "").replace(/^#/, "")
    if (id) history.replaceState(null, "", `#${id}`)
  }

  scheduleSync() {
    if (this.spyFrame) return

    this.spyFrame = requestAnimationFrame(() => {
      this.spyFrame = null
      this.syncActive()
    })
  }

  syncActive() {
    if (this.tagTargets.length === 0) return
    if (this.pinnedTag) return this.markActive(this.pinnedTag)

    const scroller = scrollContainerFor(this.element)
    if (this.scrollerAtBottom(scroller)) {
      return this.markActive(this.tagTargets[this.tagTargets.length - 1])
    }

    const marker = this.activeMarker()
    let current = this.tagTargets[0]
    this.tagTargets.forEach((tag) => {
      const section = this.sectionFor(tag)
      if (!section) return
      if (section.getBoundingClientRect().top <= marker + 1) current = tag
    })
    this.markActive(current)
  }

  markActive(tag) {
    this.tagTargets.forEach((other) => {
      const active = other === tag
      other.classList.toggle("is-active", active)
      if (active) {
        other.setAttribute("aria-current", "true")
      } else {
        other.removeAttribute("aria-current")
      }
    })
  }

  sectionFor(tag) {
    const id = (tag.getAttribute("href") || "").replace(/^#/, "")
    return id ? document.getElementById(id) : null
  }

  // A section counts as current once its top passes just below the sticky
  // row. Measured live so it stays correct if the row's height changes.
  // The slack must exceed the gap between the row and scroll-margin-top,
  // or a jumped-to section sits a fraction of a pixel past the marker.
  activeMarker() {
    if (!this.hasChromeTarget) return 80

    return this.chromeTarget.getBoundingClientRect().bottom + 24
  }

  scrollerAtBottom(scroller) {
    return scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
  }

  scrollBehavior() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
  }
}
